import React, { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "components/Toast";
import Skeleton from "components/Skeleton";
import { Eye, Save, Plus, Braces } from "lucide-react";
import {
  useNotificationTemplates,
  useCreateNotificationTemplate,
  useUpdateNotificationTemplate,
  useNotificationTemplatePreview,
} from "hooks/crm/useNotificationAdmin";

const CHANNEL_LABELS = { telegram: "Telegram", slack: "Slack", inApp: "In-app" };

// Parse {{var}} tokens (dedupe, in appearance order).
function parseVars(...strings) {
  const seen = new Set();
  const out = [];
  const re = /\{\{\s*([\w.]+)\s*\}\}/g;
  for (const s of strings) {
    let m;
    while ((m = re.exec(s || ""))) {
      if (!seen.has(m[1])) {
        seen.add(m[1]);
        out.push(m[1]);
      }
    }
  }
  return out;
}

/**
 * ChannelTemplateEditor (SOUND-523) — authors the active template for one
 * (event × channel): subject? + body ({{var}}), a var palette that inserts at
 * the cursor, and a live server-rendered preview (byte-accurate to what sends).
 * One active per (eventKey, channel) is enforced by the API; save is
 * POST (new) or PUT (existing).
 */
export default function ChannelTemplateEditor({ eventKey, channel }) {
  const { toast } = useToast();
  const { data: templates = [], isLoading } = useNotificationTemplates({ eventKey, channel });
  const createTemplate = useCreateNotificationTemplate();
  const updateTemplate = useUpdateNotificationTemplate();
  const preview = useNotificationTemplatePreview();

  const active = useMemo(
    () => templates.find((t) => t.active) || templates[0] || null,
    [templates]
  );

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [seeded, setSeeded] = useState(null); // templateId whose values are loaded
  const [previewText, setPreviewText] = useState("");
  const bodyRef = useRef(null);
  const subjectRef = useRef(null);
  const lastFocused = useRef("body");

  // Seed from the loaded active template (once per template identity).
  useEffect(() => {
    const id = active?.templateId || "__new__";
    if (seeded === id) return;
    setSubject(active?.subject || "");
    setBody(active?.body || "");
    setSeeded(id);
  }, [active, seeded]);

  const vars = useMemo(() => parseVars(subject, body), [subject, body]);

  // Debounced live preview.
  const runPreview = preview.mutate;
  useEffect(() => {
    if (!body && !subject) {
      setPreviewText("");
      return;
    }
    const t = setTimeout(() => {
      const sampleVars = {};
      parseVars(subject, body).forEach((v) => (sampleVars[v] = `‹${v}›`));
      runPreview(
        { channel, subject: subject || undefined, body, vars: sampleVars },
        { onSuccess: (text) => setPreviewText(text || "") }
      );
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, body, channel]);

  const insertVar = (name) => {
    const token = `{{${name}}}`;
    if (lastFocused.current === "subject") {
      const el = subjectRef.current;
      const at = el?.selectionStart ?? subject.length;
      setSubject((s) => s.slice(0, at) + token + s.slice(el?.selectionEnd ?? at));
    } else {
      const el = bodyRef.current;
      const at = el?.selectionStart ?? body.length;
      setBody((b) => b.slice(0, at) + token + b.slice(el?.selectionEnd ?? at));
    }
  };

  const [newVar, setNewVar] = useState("");
  const addNewVar = () => {
    const name = newVar.trim().replace(/[^\w.]/g, "");
    if (name) insertVar(name);
    setNewVar("");
  };

  const dirty = (active?.subject || "") !== subject || (active?.body || "") !== body;

  const save = () => {
    if (!body.trim()) {
      toast({ status: "warning", title: "Template body is required" });
      return;
    }
    if (active?.templateId) {
      updateTemplate.mutate(
        { templateId: active.templateId, updates: { subject: subject || undefined, body } },
        {
          onSuccess: () => toast({ status: "success", title: `${CHANNEL_LABELS[channel]} template saved` }),
          onError: (err) =>
            toast({
              status: "destructive",
              title: "Save failed",
              description: err?.response?.data?.error || "Unknown error",
            }),
        }
      );
    } else {
      createTemplate.mutate(
        { eventKey, channel, subject: subject || undefined, body },
        {
          onSuccess: () => toast({ status: "success", title: `${CHANNEL_LABELS[channel]} template created` }),
          onError: (err) =>
            toast({
              status: "destructive",
              title: "Create failed",
              description: err?.response?.data?.error || "Unknown error",
            }),
        }
      );
    }
  };

  if (isLoading) return <Skeleton.Rect className="h-40 w-full" />;

  const saving = createTemplate.isLoading || updateTemplate.isLoading;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Editor */}
        <div className="space-y-3">
          <label className="flex flex-col text-sm">
            <span className="mb-1 font-medium text-foreground">
              Subject <span className="font-normal text-muted-foreground">(optional)</span>
            </span>
            <input
              ref={subjectRef}
              className="input"
              placeholder="Rendered bold on Telegram; title on in-app"
              value={subject}
              onFocus={() => (lastFocused.current = "subject")}
              onChange={(e) => setSubject(e.target.value)}
            />
          </label>
          <label className="flex flex-col text-sm">
            <span className="mb-1 font-medium text-foreground">Body</span>
            <textarea
              ref={bodyRef}
              className="textarea min-h-[160px] font-mono text-xs"
              placeholder={"Message body — use {{variables}} for context."}
              value={body}
              onFocus={() => (lastFocused.current = "body")}
              onChange={(e) => setBody(e.target.value)}
            />
          </label>

          {/* Variable palette */}
          <div className="rounded-md border border-border bg-muted/20 p-2.5">
            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <Braces className="h-3.5 w-3.5" />
              Variables
            </div>
            {vars.length > 0 ? (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {vars.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => insertVar(v)}
                    className="badge badge-brand cursor-pointer font-mono"
                    title={`Insert {{${v}}}`}
                  >
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
            ) : (
              <p className="mb-2 text-xs text-muted-foreground">
                Add a variable below, or type <span className="font-mono">{"{{name}}"}</span> in the body.
              </p>
            )}
            <div className="flex items-center gap-2">
              <input
                className="input h-8 flex-1 font-mono text-xs"
                placeholder="variableName"
                value={newVar}
                onChange={(e) => setNewVar(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addNewVar();
                  }
                }}
              />
              <button type="button" className="btn btn-outline h-8 text-xs" onClick={addNewVar}>
                <Plus className="h-3.5 w-3.5" />
                Insert
              </button>
            </div>
          </div>

          <button className="btn btn-primary w-fit" onClick={save} disabled={saving || !dirty}>
            <Save className="h-4 w-4" />
            {saving ? "Saving…" : active?.templateId ? "Save template" : "Create template"}
          </button>
        </div>

        {/* Preview */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <Eye className="h-3.5 w-3.5" />
            Live preview
            {preview.isLoading && <span className="font-normal normal-case text-muted-foreground">· rendering…</span>}
          </div>
          <div className="min-h-[160px] rounded-lg border border-border bg-card p-4">
            {previewText ? (
              <div className="whitespace-pre-wrap break-words text-sm text-foreground">{previewText}</div>
            ) : (
              <p className="text-sm text-muted-foreground">
                The rendered message appears here (sample values shown as ‹var›).
              </p>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Byte-accurate to what {CHANNEL_LABELS[channel]} sends. Sample values shown for variables.
          </p>
        </div>
      </div>
    </div>
  );
}
